import React, { useEffect, useState } from 'react';
import './index.scss';
import Icon from './Icon';

export const LocationCard = ({ className = '', location }) => {
  const locationCardClassName = ['location-card', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={locationCardClassName}>
      <div className="icon-outer-con">
        <div className="icon-inner-con" />
      </div>
      <div>
        <h6>Dine-In at Restaurant</h6>
        <p>{location}</p>
      </div>
    </div>
  );
};

export function FooterButton({
  onClick,
  className,
  buttonClassName,
  children
}) {
  const footerClassName = ['avengers__footer', className]
    .filter(Boolean)
    .join(' ');
  const btnClassName = ['avengers__pre-book-btn', buttonClassName]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={footerClassName}>
      <MealButton onClick={onClick} className={btnClassName}>
        {children}
      </MealButton>
    </div>
  );
}

const calculateTimeRemaining = endDate => {
  const difference = new Date(endDate).getTime() - Date.now();

  if (difference <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0
    };
  }

  const totalSeconds = Math.floor(difference / 1000);

  return {
    days: Math.floor(totalSeconds / (24 * 60 * 60)),

    hours: Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60)),

    minutes: Math.floor((totalSeconds % (60 * 60)) / 60),

    seconds: totalSeconds % 60
  };
};

export const CountdownTimer = ({ endDate, className = '' }) => {
  const [timeRemaining, setTimeRemaining] = useState(() =>
    calculateTimeRemaining(endDate)
  );

  useEffect(() => {
    const interval = window.setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(endDate));
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [endDate]);

  const timeUnits = [
    {
      value: timeRemaining.days,
      label: 'DAYS'
    },
    {
      value: timeRemaining.hours,
      label: 'HRS'
    },
    {
      value: timeRemaining.minutes,
      label: 'MIN'
    },
    {
      value: timeRemaining.seconds,
      label: 'SEC'
    }
  ];

  return (
    <div className={`countdown ${className}`}>
      {timeUnits.map(unit => (
        <div className="countdown__item" key={unit.label}>
          <span className="countdown__value">
            {unit.value.toString().padStart(2, '0')}
          </span>

          <span className="countdown__label">{unit.label}</span>
        </div>
      ))}
    </div>
  );
};

export const AddRemoveButtons = ({
  className,
  quantity,
  disabled,
  onQuantityChange,
  maxQuantity
}) => {
  const buttonClassName = [className, 'btn--quantity']
    .filter(Boolean)
    .join(' ');

  const handleDecrement = () => {
    if (disabled || !onQuantityChange || typeof onQuantityChange !== 'function')
      return;

    onQuantityChange(Math.max(0, quantity - 1));
  };

  const handleIncrement = () => {
    if (disabled || !onQuantityChange) return;

    if (
      (maxQuantity !== undefined && quantity >= maxQuantity) ||
      typeof onQuantityChange !== 'function'
    ) {
      return;
    }

    onQuantityChange(quantity + 1);
  };
  return (
    <div className={buttonClassName} aria-label={`Quantity: ${quantity}`}>
      <button
        type="button"
        className="btn-quantity__action"
        onClick={handleDecrement}
        disabled={disabled || !onQuantityChange}
        aria-label="Decrease quantity"
      >
        <span aria-hidden="true">
          <Icon name="minus" stokeWidth={3} />
        </span>
      </button>

      <span
        className="btn-quantity__value"
        aria-live="polite"
        aria-atomic="true"
      >
        {quantity}
      </span>

      <button
        type="button"
        className="btn-quantity__action"
        onClick={handleIncrement}
        disabled={
          disabled ||
          !onQuantityChange ||
          (maxQuantity !== undefined && quantity >= maxQuantity)
        }
        aria-label="Increase quantity"
      >
        <span aria-hidden="true">
          <Icon name="plus" stokeWidth={3} />
        </span>
      </button>
    </div>
  );
};

export const MealButton = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  className = '',
  startIcon,
  endIcon,
  disabled = false,
  quantity = 0,
  onQuantityChange,
  maxQuantity,
  ...props
}) => {
  const isQuantityMode = quantity > 0 && !loading;

  const buttonClassName = ['btn', `btn-${variant}`, `btn-${size}`, className]
    .filter(Boolean)
    .join(' ');

  if (isQuantityMode) {
    return (
      <AddRemoveButtons
        className={buttonClassName}
        disabled={disabled}
        maxQuantity={maxQuantity}
        onQuantityChange={onQuantityChange}
        quantity={quantity}
      />
    );
  }

  return (
    <button
      type="button"
      className={buttonClassName}
      disabled={disabled || loading}
      {...props} // eslint-disable-line react/jsx-props-no-spreading
    >
      {loading ? (
        <span className="btn__loader" />
      ) : (
        <>
          {startIcon && (
            <span className="btn__icon btn__icon--start">{startIcon}</span>
          )}

          <span className="btn__text">{children}</span>

          {endIcon && (
            <span className="btn__icon btn__icon--end">{endIcon}</span>
          )}
        </>
      )}
    </button>
  );
};
